import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from "react";
import {
  isSupabaseConnected,
  signInCustomer,
  signUpCustomer,
  signOutAdmin as signOut,
  getInitialSession,
  onAuthStateChange,
  fetchCustomerProfile,
  fetchCustomerOrders,
} from "./supabase";

export interface CustomerProfile {
  id: string;
  email: string;
  display_name: string | null;
  country: string | null;
  created_at: string;
}

export interface CustomerOrder {
  id: string;
  order_number: string;
  status: string;
  subtotal: number;
  discount_amount: number;
  delivery: string;
  created_at: string;
  order_items: { product_name: string; quantity: number; unit_price: number }[];
}

interface CustomerAuthContextType {
  customer: CustomerProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAvailable: boolean; // whether Supabase is connected
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUp: (email: string, password: string, displayName?: string) => Promise<{ success: boolean; error?: string }>;
  logOut: () => void;
  orders: CustomerOrder[];
  loadOrders: () => Promise<void>;
}

const CustomerAuthContext = createContext<CustomerAuthContextType | null>(null);

export function CustomerAuthProvider({ children }: { children: ReactNode }) {
  const [customer, setCustomer] = useState<CustomerProfile | null>(null);
  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const mountedRef = useRef(true);
  const available = isSupabaseConnected();

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  // Check existing session on mount (use getInitialSession to avoid auth lock race with Strict Mode)
  useEffect(() => {
    if (!available) {
      setIsLoading(false);
      return;
    }

    getInitialSession().then(async ({ data }) => {
      if (!mountedRef.current) return;
      const session = data?.session;
      if (session?.user) {
        const { data: profile } = await fetchCustomerProfile(session.user.id);
        if (mountedRef.current && profile) {
          setCustomer(profile);
        }
      }
      if (mountedRef.current) setIsLoading(false);
    });

    // Do not await Supabase inside callback — causes auth lock. Schedule profile fetch outside.
    const { data: sub } = onAuthStateChange((event: string, session: any) => {
      if (!mountedRef.current) return;
      if (event === "SIGNED_OUT") {
        setCustomer(null);
        setOrders([]);
        return;
      }
      if (event === "SIGNED_IN" && session?.user?.id) {
        const authUid = session.user.id;
        setTimeout(() => {
          if (!mountedRef.current) return;
          fetchCustomerProfile(authUid).then(({ data: profile }) => {
            if (mountedRef.current && profile) setCustomer(profile);
          });
        }, 0);
      }
    });

    return () => { sub?.subscription?.unsubscribe(); };
  }, [available]);

  const signIn = async (email: string, password: string) => {
    if (!available) return { success: false, error: "Authentication not available" };
    const { data, error } = await signInCustomer(email, password);
    if (error) return { success: false, error: error.message };
    if (data?.user) {
      const { data: profile } = await fetchCustomerProfile(data.user.id);
      if (profile) setCustomer(profile);
      return { success: true };
    }
    return { success: false, error: "Sign in failed" };
  };

  const signUp = async (email: string, password: string, displayName?: string) => {
    if (!available) return { success: false, error: "Authentication not available" };
    const { data, error } = await signUpCustomer(email, password, displayName);
    if (error) return { success: false, error: error.message };
    if (data?.user) {
      const { data: profile } = await fetchCustomerProfile(data.user.id);
      if (profile) setCustomer(profile);
      return { success: true };
    }
    return { success: true }; // email confirmation may be required
  };

  const logOut = () => {
    signOut().catch(() => {});
    setCustomer(null);
    setOrders([]);
  };

  const loadOrders = async () => {
    if (!customer) return;
    const { data } = await fetchCustomerOrders(customer.email);
    if (data && mountedRef.current) setOrders(data);
  };

  return (
    <CustomerAuthContext.Provider
      value={{
        customer,
        isAuthenticated: !!customer,
        isLoading,
        isAvailable: available,
        signIn,
        signUp,
        logOut,
        orders,
        loadOrders,
      }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) {
    // Return safe fallback when used outside provider
    return {
      customer: null,
      isAuthenticated: false,
      isLoading: false,
      isAvailable: false,
      signIn: async () => ({ success: false, error: "Not available" } as const),
      signUp: async () => ({ success: false, error: "Not available" } as const),
      logOut: () => {},
      orders: [] as CustomerOrder[],
      loadOrders: async () => {},
    };
  }
  return ctx;
}
